import { IMembership } from "api/models/Membership"
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { User } from "./types/User"

const Context = createContext({} as any)

export type ShareData = {
  shareToken: string
  resourceType: string
  resourceId: string
  organizationId: string
  createdAt: string
  createdBy: {
    email: string
    name: string
  }
}

export type AppContext = {
  loading: boolean
  user?: User
  setUser: (userData: any) => void
  isSuperAdmin: boolean
  membership?: IMembership
  setMembership: (data: IMembership) => void
  orgId: string
  token: string
  setToken: (token: string | undefined) => void
  toasts: ToastItem[]
  addToast: (header: string, body: string, type?: ToastType) => void
  deleteToast: (toast: ToastItem) => void
  isAnonymous: boolean
  setIsAnonymous: (isAnonymous: boolean) => void
  shareData?: ShareData
  setShareData: (shareData: ShareData | undefined) => void
  anonToken?: string
}

export const useApp = (): AppContext => useContext(Context)

type Props = {
  children: React.ReactNode
}

export type ToastItem = {
  header: string
  timestamp: number
  body: string
  type: ToastType
}

export type ToastType = "success" | "error" | "info" | "warning"

export const AppProvider: React.FC<Props> = ({ children }: Props) => {
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState<User | undefined>(undefined)
  const [membership, setMembership] = useState<IMembership | undefined>(undefined)
  const [token, setToken] = useState<string | undefined>(undefined)
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const [isAnonymous, setIsAnonymous] = useState(false)
  const [shareData, setShareData] = useState<ShareData | undefined>(undefined)

  // Compute anonToken from shareData
  const anonToken = useMemo(() => shareData?.shareToken, [shareData])

  useEffect(() => {
    setToken(localStorage.getItem("token") || "")
  }, [])

  useEffect(() => {
    if (user !== undefined) setLoading(false)
  }, [user])

  const isSuperAdmin = useMemo(() => {
    return user?.superAdmin || false
  }, [user])

  const orgId = useMemo(() => {
    // In anonymous mode, use organization ID from share data
    if (shareData?.organizationId) {
      return shareData.organizationId
    }
    // Otherwise use membership organization
    return membership?.organization?.toString() || ""
  }, [membership, shareData])

  const setTokenCallback = useCallback((token: string) => {
    setToken(token)
    if (token === undefined) {
      localStorage.removeItem("token")
    } else {
      localStorage.setItem("token", token)
    }
  }, [])

  const addToast = useCallback((header: string, body: string, type: ToastType = "success") => {
    setToasts((value) => [
      ...value,
      {
        header,
        body,
        timestamp: Date.now(),
        type,
      },
    ])
  }, [])

  const deleteToast = useCallback(
    (toast: ToastItem) => {
      const index = toasts.indexOf(toast)
      if (index === -1) return
      const newItems = [...toasts]
      newItems.splice(index, 1)
      setToasts(newItems)
    },
    [toasts],
  )

  useEffect(() => {
    const iid = setInterval(() => {
      for (let i = 0; i < toasts.length; i++) {
        const diff = Date.now() - toasts[i].timestamp
        const fourSeconds = 4000
        if (diff >= fourSeconds) {
          return deleteToast(toasts[i])
        }
      }
    }, 100)

    return () => clearInterval(iid)
  }, [toasts, deleteToast])

  return (
    <Context.Provider
      value={{
        loading,
        user,
        setUser,
        membership,
        setMembership,
        orgId,
        token,
        setToken: setTokenCallback,
        toasts,
        addToast,
        deleteToast,
        isSuperAdmin,
        isAnonymous,
        setIsAnonymous,
        shareData,
        setShareData,
        anonToken,
      }}>
      {children}
    </Context.Provider>
  )
}
